import axios, { isAxiosError } from 'axios';
import type { AxiosInstance, AxiosRequestConfig, Method, AxiosResponse } from 'axios';

import { Constants } from '@/src/utils';

export const AUTH_EXPIRED_EVENT = 'auth:expired';

const axiosClient: AxiosInstance = axios.create({
    baseURL: Constants.API_BASE_URL,
    withCredentials: true,
});

type ApiMethod = Method;
type ApiBody = object | undefined;

// This method should be used to handle all API requests
async function apiRequest<T>(
    method: ApiMethod,
    route: string,
    body?: ApiBody,
    headers?: Record<string, string>
): Promise<AxiosResponse<T>> {
    const config: AxiosRequestConfig = {
        method,
        url: route,
        data: body,
        headers: {
            'Content-Type': headers?.['Content-Type'] || 'application/json',
            ...headers,
        },
        responseType: (headers?.['responseType'] || 'json') as AxiosRequestConfig['responseType'],
    };
    try {
        const response = await axiosClient.request(config);
        return response;
    } catch (error: unknown) {
        if (isAxiosError(error) && error.response && error.response.status === 401) {
            // Authentication failed. Try to refresh the auth token
            if (error.response.data && error.response.data.type === 'LoginFailed') {
                error.message = error.response.data.errorMessage || 'Unknown error.';
                throw error;
            }
            try {
                await axiosClient.post('/auth/getAuthToken');
                // Retry original request with new token
                const retryResponse = await axiosClient.request(config);
                return retryResponse;
            } catch (refreshError: unknown) {
                if (
                    isAxiosError(refreshError) &&
                    refreshError.response &&
                    refreshError.response.status === 401
                ) {
                    refreshError.message = 'Session expired. Please log in again.';
                    throw refreshError;
                } else {
                    throw refreshError;
                }
            }
        }
        throw error;
    }
}

export default apiRequest;
